const normalizedProfile = (profile) =>
  String(profile || '').replace(/^cast-/, '');

export const buildLiveCastTransportUrls = (
  selectedUrl,
  profile,
  origin = window.location.origin
) => {
  const outputProfile = normalizedProfile(profile);
  if (!selectedUrl || !outputProfile) return null;

  let resume;
  try {
    resume = new URL(selectedUrl, origin);
  } catch {
    return null;
  }
  resume.searchParams.set('output_format', 'mpegts');
  resume.searchParams.set('adaptive_output_profile', outputProfile);
  resume.searchParams.delete('output_profile');

  // DISPATCHARR_LIVE_SAME_PROFILE_CAST_TEE_R8_23_0
  const requestedDelivery = resume.searchParams.get('adaptive_delivery');
  const adaptiveDelivery =
    requestedDelivery === 'direct'
      ? 'direct'
      : requestedDelivery === 'audio_only'
        ? 'audio_only'
        : 'transcode';
  const castProfileKey =
    adaptiveDelivery === 'audio_only'
      ? `audio-only-${outputProfile}`
      : outputProfile;
  const bootstrap = new URL(resume.toString());
  bootstrap.searchParams.set('adaptive_delivery', 'transcode');
  bootstrap.searchParams.set('adaptive_output_profile', outputProfile);
  bootstrap.searchParams.delete('output_profile');

  return {
    adaptiveDelivery,
    castProfileKey,
    // Existing transcode/audio_only FFmpeg already has the HLS tee. Only raw
    // direct Web playback needs a temporary same-tier bootstrap on Cast click.
    requiresBootstrap: adaptiveDelivery === 'direct',
    resumeUrl: resume.toString(),
    bootstrapUrl: bootstrap.toString(),
  };
};

const abortBootstrap = (controller) => {
  try {
    controller?.abort?.();
  } catch {
    // Cleanup is best-effort; the lease remains the authoritative owner.
  }
};

// Keep the direct Web client attached until CAF owns a playable HLS stream.
// The temporary HTTP bootstrap exists only when Web selected raw delivery;
// the Cast lease retains the OutputProfile manager after it is aborted.
export const runLiveCastHandoff = async ({
  requiresBootstrap,
  startLease,
  stopLease,
  startBootstrap,
  prewarm,
  loadReceiver,
  // DISPATCHARR_CAST_TRANSITION_OPS_R134: throws once a newer Cast action
  // superseded this handoff; the catch below then releases its lease.
  checkpoint = () => {},
}) => {
  let leaseStarted = false;
  let bootstrapController = null;
  try {
    await startLease();
    leaseStarted = true;
    checkpoint();
    if (requiresBootstrap) {
      bootstrapController = await startBootstrap();
      checkpoint();
    }
    const prewarmedSegments = await prewarm();
    checkpoint();
    const receiver = await loadReceiver();
    abortBootstrap(bootstrapController);
    bootstrapController = null;
    return { prewarmedSegments, receiver };
  } catch (error) {
    abortBootstrap(bootstrapController);
    bootstrapController = null;
    if (leaseStarted) {
      try {
        await stopLease();
      } catch {
        // Never replace the actual handoff failure with a cleanup failure.
      }
    }
    throw error;
  }
};
